import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Activity, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [status, setStatus] = useState('polling'); // polling, paid, error
  const [planInfo, setPlanInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      if (attempts >= 10) {
        setStatus('error');
        return;
      }

      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          setPlanInfo(res.data);
          await checkAuth();
        } else if (res.data.status === 'expired') {
          setStatus('error');
        } else {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        }
      } catch {
        setTimeout(() => setAttempts(prev => prev + 1), 2000);
      }
    };

    pollStatus();
  }, [sessionId, attempts, checkAuth]);

  const handleGoDashboard = () => {
    if (planInfo?.plan_type === 'company') {
      navigate('/rh');
    } else {
      navigate('/student');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="payment-success-page">
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Activity className="w-7 h-7 text-blue-600" />
          <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Vital</span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          {status === 'polling' && (
            <div data-testid="payment-polling">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Processando Pagamento...
              </h1>
              <p className="text-slate-600">Aguarde enquanto confirmamos seu pagamento.</p>
            </div>
          )}

          {status === 'paid' && (
            <div data-testid="payment-confirmed">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Pagamento Confirmado!
              </h1>
              <p className="text-slate-600 mb-8">
                Seu plano foi ativado com sucesso. Aproveite todos os recursos!
              </p>
              <Button
                onClick={handleGoDashboard}
                data-testid="go-dashboard-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                size="lg"
              >
                Ir para o Dashboard
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="payment-error">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Erro no Pagamento
              </h1>
              <p className="text-slate-600 mb-8">
                Não foi possível confirmar seu pagamento. Tente novamente ou entre em contato.
              </p>
              <Button
                onClick={() => navigate('/')}
                data-testid="go-home-btn"
                variant="outline"
                size="lg"
              >
                Voltar ao Início
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
